import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useRef, useState, type ChangeEvent, type FormEvent } from "react";

import { resolveApiUrl } from "../config/environment";
import { isApiClientError } from "../api/errors";
import type { JsonValue } from "../api";
import type {
  SiteContentCategory,
  SiteContentApi,
  SiteContentPage,
  SiteContentSettings,
} from "../entities/site-content";
import { EmptyState } from "./states";
import { Select } from "./Select";
import { Notice } from "./Notice";

export type SiteContentServices = Pick<
  SiteContentApi,
  | "listCategories"
  | "createCategory"
  | "updateCategory"
  | "deleteCategory"
  | "listPages"
  | "page"
  | "createPage"
  | "updatePage"
  | "deletePage"
  | "settings"
  | "updateSettings"
  | "uploadMedia"
  | "deleteMedia"
>;

function siteError(error: unknown, fallback: string): string {
  if (isApiClientError(error)) {
    if (error.status === 409)
      return "Данные уже изменились или используются. Обновите список и повторите действие.";
    if (error.status === 422)
      return "Проверьте заполнение полей: сервер не принял данные.";
    if (error.status === 403) return "Недостаточно прав для этого действия.";
    if (error.status === 503)
      return "Сервис временно недоступен. Ничего не потеряно — повторите позже.";
  }
  return fallback;
}

function slugify(value: string): string {
  return value
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "");
}

function CategoryForm({
  category,
  onSubmit,
  pending,
  onCancel,
}: {
  category?: SiteContentCategory;
  onSubmit: (input: {
    name: string;
    slug?: string;
    description: string | null;
    sort_order: number;
    is_active: boolean;
    seo_title: string | null;
    seo_description: string | null;
  }) => void;
  pending: boolean;
  onCancel?: () => void;
}) {
  const [name, setName] = useState(category?.name ?? "");
  const [slug, setSlug] = useState(category?.slug ?? "");
  const [description, setDescription] = useState(category?.description ?? "");
  const [sortOrder, setSortOrder] = useState(String(category?.sort_order ?? 0));
  const [seoTitle, setSeoTitle] = useState(category?.seo.title ?? "");
  const [seoDescription, setSeoDescription] = useState(
    category?.seo.description ?? "",
  );
  const [active, setActive] = useState(category?.is_active ?? true);

  return (
    <form
      className="site-content-form"
      onSubmit={(event) => {
        event.preventDefault();
        onSubmit({
          name: name.trim(),
          ...(slug.trim() ? { slug: slugify(slug) } : {}),
          description: description.trim() || null,
          sort_order: Number(sortOrder),
          is_active: active,
          seo_title: seoTitle.trim() || null,
          seo_description: seoDescription.trim() || null,
        });
      }}
    >
      <div className="site-content-form__grid">
        <label>
          Название
          <input
            required
            value={name}
            onChange={(event) => setName(event.target.value)}
          />
        </label>
        <label>
          Slug
          <input
            value={slug}
            onChange={(event) => setSlug(event.target.value)}
            pattern="[a-z0-9]+(-[a-z0-9]+)*"
            placeholder="novosti"
          />
        </label>
        <label>
          Порядок
          <input
            required
            min="0"
            max="100000"
            type="number"
            value={sortOrder}
            onChange={(event) => setSortOrder(event.target.value)}
          />
        </label>
        <label className="site-content-form__toggle">
          <input
            type="checkbox"
            checked={active}
            onChange={(event) => setActive(event.target.checked)}
          />
          Показывать на сайте
        </label>
      </div>
      <label>
        Описание
        <textarea
          value={description}
          maxLength={5000}
          rows={3}
          onChange={(event) => setDescription(event.target.value)}
        />
      </label>
      <details className="site-content-form__details">
        <summary>SEO-параметры</summary>
        <div className="site-content-form__grid">
          <label>
            SEO-заголовок
            <input
              value={seoTitle}
              maxLength={255}
              onChange={(event) => setSeoTitle(event.target.value)}
            />
          </label>
          <label>
            SEO-описание
            <textarea
              value={seoDescription}
              maxLength={500}
              rows={2}
              onChange={(event) => setSeoDescription(event.target.value)}
            />
          </label>
        </div>
      </details>
      <div className="inline-actions">
        <button
          className="button button--primary"
          type="submit"
          disabled={pending}
        >
          {pending
            ? "Сохраняем…"
            : category
              ? "Сохранить категорию"
              : "Добавить категорию"}
        </button>
        {onCancel ? (
          <button
            className="button button--secondary"
            type="button"
            onClick={onCancel}
          >
            Отмена
          </button>
        ) : null}
      </div>
    </form>
  );
}

export function SiteCategoriesPage({
  services,
  canManage,
}: {
  services: SiteContentServices;
  canManage: boolean;
}) {
  const client = useQueryClient();
  const [editing, setEditing] = useState<SiteContentCategory | null>(null);
  const [creating, setCreating] = useState(false);
  const categories = useQuery({
    queryKey: ["site", "categories"],
    queryFn: () => services.listCategories(),
    retry: false,
  });
  const refresh = () =>
    client.invalidateQueries({ queryKey: ["site", "categories"] });
  const create = useMutation({
    mutationFn: services.createCategory,
    onSuccess: async () => {
      await refresh();
      setCreating(false);
    },
  });
  const update = useMutation({
    mutationFn: ({
      id,
      input,
    }: {
      id: string;
      input: Parameters<SiteContentServices["updateCategory"]>[1];
    }) => services.updateCategory(id, input),
    onSuccess: async () => {
      await refresh();
      setEditing(null);
    },
  });
  const remove = useMutation({
    mutationFn: services.deleteCategory,
    onSuccess: refresh,
  });
  const mutationError = create.error ?? update.error ?? remove.error;

  return (
    <main
      className="content-page site-content-page"
      aria-labelledby="site-categories-title"
    >
      <header className="page-header">
        <div>
          <p className="eyebrow">Публичный сайт</p>
          <h1 id="site-categories-title">Категории новостей</h1>
          <p>
            Активные категории используются в фильтрах и публикациях. Удаление
            недоступно, пока категория используется.
          </p>
        </div>
        {canManage ? (
          <button
            className="button button--primary"
            type="button"
            onClick={() => {
              setCreating(true);
              setEditing(null);
            }}
          >
            Добавить категорию
          </button>
        ) : null}
      </header>
      {mutationError ? (
        <Notice>
          {siteError(mutationError, "Не удалось сохранить категорию.")}
        </Notice>
      ) : null}
      {creating ? (
        <section className="site-content-panel">
          <h2>Новая категория</h2>
          <CategoryForm
            pending={create.isPending}
            onCancel={() => setCreating(false)}
            onSubmit={(input) => create.mutate(input)}
          />
        </section>
      ) : null}
      {categories.isLoading ? (
        <p aria-busy="true">Загружаем категории…</p>
      ) : null}
      {categories.isError ? (
        <Notice>Не удалось загрузить категории.</Notice>
      ) : null}
      {!categories.isLoading &&
      !categories.isError &&
      !categories.data?.data.length ? (
        <EmptyState
          title="Категорий пока нет"
          description="Добавьте категорию, чтобы систематизировать публикации на сайте."
          action={
            canManage
              ? {
                  label: "Добавить категорию",
                  onClick: () => setCreating(true),
                }
              : undefined
          }
        />
      ) : null}
      {categories.data?.data.length ? (
        <div className="site-category-list">
          {categories.data.data.map((category) => (
            <article className="site-content-panel" key={category.id}>
              <header className="site-content-panel__header">
                <div>
                  <h2>{category.name}</h2>
                  <p>
                    /{category.slug} · порядок {category.sort_order}
                  </p>
                </div>
                <span
                  className={`status-badge status-badge--${category.is_active ? "ready" : "expired"}`}
                >
                  {category.is_active ? "Активна" : "Скрыта"}
                </span>
              </header>
              {editing?.id === category.id ? (
                <CategoryForm
                  category={category}
                  pending={update.isPending}
                  onCancel={() => setEditing(null)}
                  onSubmit={(input) =>
                    update.mutate({ id: category.id, input })
                  }
                />
              ) : (
                <>
                  <p>{category.description || "Описание не добавлено."}</p>
                  <div className="inline-actions">
                    {canManage ? (
                      <button
                        className="button button--secondary button--compact"
                        type="button"
                        onClick={() => {
                          setEditing(category);
                          setCreating(false);
                        }}
                      >
                        Изменить
                      </button>
                    ) : null}
                    {canManage ? (
                      <button
                        className="button button--text button--danger"
                        type="button"
                        disabled={remove.isPending}
                        onClick={() => {
                          if (
                            window.confirm(
                              `Удалить категорию «${category.name}»?`,
                            )
                          )
                            remove.mutate(category.id);
                        }}
                      >
                        Удалить
                      </button>
                    ) : null}
                  </div>
                </>
              )}
            </article>
          ))}
        </div>
      ) : null}
    </main>
  );
}

function pageContent(page?: SiteContentPage): string {
  if (!page) return "";
  return page.content_format === "html"
    ? (page.content_html ?? "")
    : JSON.stringify(page.content ?? [], null, 2);
}

function PageForm({
  page,
  pending,
  onCancel,
  onSubmit,
}: {
  page?: SiteContentPage;
  pending: boolean;
  onCancel: () => void;
  onSubmit: (input: {
    key: string;
    slug: string;
    title: string;
    content_format: "html" | "json";
    content_html?: string | null;
    content?: (string | Record<string, JsonValue>)[] | null;
    publish: boolean;
    seo_title: string | null;
    seo_description: string | null;
  }) => void;
}) {
  const [key, setKey] = useState(page?.key ?? "");
  const [slug, setSlug] = useState(page?.slug ?? "");
  const [title, setTitle] = useState(page?.title ?? "");
  const [format, setFormat] = useState<"html" | "json">(
    page?.content_format ?? "html",
  );
  const [content, setContent] = useState(pageContent(page));
  const [publish, setPublish] = useState(page?.status === "published");
  const [seoTitle, setSeoTitle] = useState(page?.seo.title ?? "");
  const [seoDescription, setSeoDescription] = useState(
    page?.seo.description ?? "",
  );
  const [contentError, setContentError] = useState<string | null>(null);
  const submit = (event: FormEvent) => {
    event.preventDefault();
    let json: (string | Record<string, JsonValue>)[] | null = null;
    if (format === "json") {
      try {
        const parsed: unknown = JSON.parse(content || "[]");
        if (
          !Array.isArray(parsed) ||
          !parsed.every(
            (item) =>
              typeof item === "string" ||
              (item !== null &&
                typeof item === "object" &&
                !Array.isArray(item)),
          )
        )
          throw new Error();
        json = parsed as (string | Record<string, JsonValue>)[];
        setContentError(null);
      } catch {
        setContentError(
          "Для JSON-контента нужен массив строк или объектов блоков.",
        );
        return;
      }
    }
    onSubmit({
      key: key.trim(),
      slug: slugify(slug),
      title: title.trim(),
      content_format: format,
      ...(format === "html" ? { content_html: content } : { content: json }),
      publish,
      seo_title: seoTitle.trim() || null,
      seo_description: seoDescription.trim() || null,
    });
  };
  return (
    <form className="site-content-form" onSubmit={submit}>
      <div className="site-content-form__grid">
        <label>
          Ключ страницы
          <input
            required
            value={key}
            pattern="[a-z][a-z0-9._-]*"
            maxLength={120}
            onChange={(event) => setKey(event.target.value)}
            placeholder="contacts"
          />
        </label>
        <label>
          Slug
          <input
            required
            value={slug}
            pattern="[a-z0-9]+(-[a-z0-9]+)*"
            maxLength={180}
            onChange={(event) => setSlug(event.target.value)}
            placeholder="kontakty"
          />
        </label>
        <label>
          Заголовок
          <input
            required
            value={title}
            maxLength={255}
            onChange={(event) => setTitle(event.target.value)}
          />
        </label>
        <label>
          Формат
          <Select
            value={format}
            onChange={(event) =>
              setFormat(event.target.value as "html" | "json")
            }
          >
            <option value="html">HTML</option>
            <option value="json">JSON-блоки</option>
          </Select>
        </label>
      </div>
      <label>
        Контент
        <textarea
          required
          rows={12}
          value={content}
          spellCheck={false}
          onChange={(event) => setContent(event.target.value)}
        />
      </label>
      {format === "html" ? (
        <small>
          Ссылка на другую страницу сайта: &lt;a href="/privacy"&gt;текст&lt;/a&gt;.
        </small>
      ) : null}
      {contentError ? <Notice>{contentError}</Notice> : null}
      <label className="site-content-form__toggle">
        <input
          type="checkbox"
          checked={publish}
          onChange={(event) => setPublish(event.target.checked)}
        />
        Опубликовать после сохранения
      </label>
      <details className="site-content-form__details">
        <summary>SEO-параметры</summary>
        <div className="site-content-form__grid">
          <label>
            SEO-заголовок
            <input
              value={seoTitle}
              maxLength={255}
              onChange={(event) => setSeoTitle(event.target.value)}
            />
          </label>
          <label>
            SEO-описание
            <textarea
              value={seoDescription}
              maxLength={500}
              rows={2}
              onChange={(event) => setSeoDescription(event.target.value)}
            />
          </label>
        </div>
      </details>
      <div className="inline-actions">
        <button
          className="button button--primary"
          type="submit"
          disabled={pending}
        >
          {pending
            ? "Сохраняем…"
            : page
              ? "Сохранить страницу"
              : "Создать страницу"}
        </button>
        <button
          className="button button--secondary"
          type="button"
          onClick={onCancel}
        >
          Отмена
        </button>
      </div>
    </form>
  );
}

export function SitePagesPage({
  services,
  canManage,
}: {
  services: SiteContentServices;
  canManage: boolean;
}) {
  const queryClient = useQueryClient();
  const [selected, setSelected] = useState<SiteContentPage | null>(null);
  const [creating, setCreating] = useState(false);
  const pages = useQuery({
    queryKey: ["site", "pages"],
    queryFn: () => services.listPages(),
    retry: false,
  });
  const refresh = () =>
    queryClient.invalidateQueries({ queryKey: ["site", "pages"] });
  const create = useMutation({
    mutationFn: services.createPage,
    onSuccess: async () => {
      await refresh();
      setCreating(false);
    },
  });
  const update = useMutation({
    mutationFn: ({
      id,
      input,
    }: {
      id: string;
      input: Parameters<SiteContentServices["updatePage"]>[1];
    }) => services.updatePage(id, input),
    onSuccess: async () => {
      await refresh();
      setSelected(null);
    },
  });
  const remove = useMutation({
    mutationFn: services.deletePage,
    onSuccess: refresh,
  });
  const mutationError = create.error ?? update.error ?? remove.error;
  return (
    <main
      className="content-page site-content-page"
      aria-labelledby="site-pages-title"
    >
      <header className="page-header">
        <div>
          <p className="eyebrow">Публичный сайт</p>
          <h1 id="site-pages-title">Статические страницы</h1>
          <p>
            Страницы сохраняются как черновики или публикуются. При
            редактировании передаётся текущая версия, чтобы не перезаписать
            чужие изменения.
          </p>
        </div>
        {canManage ? (
          <button
            className="button button--primary"
            type="button"
            onClick={() => {
              setCreating(true);
              setSelected(null);
            }}
          >
            Новая страница
          </button>
        ) : null}
      </header>
      {mutationError ? (
        <Notice>
          {siteError(mutationError, "Не удалось сохранить страницу.")}
        </Notice>
      ) : null}
      {creating ? (
        <section className="site-content-panel">
          <h2>Новая статическая страница</h2>
          <PageForm
            pending={create.isPending}
            onCancel={() => setCreating(false)}
            onSubmit={(input) => create.mutate(input)}
          />
        </section>
      ) : null}
      {selected ? (
        <section className="site-content-panel">
          <h2>Редактирование: {selected.title}</h2>
          <PageForm
            page={selected}
            pending={update.isPending}
            onCancel={() => setSelected(null)}
            onSubmit={(input) =>
              update.mutate({
                id: selected.id,
                input: { ...input, version: selected.version },
              })
            }
          />
        </section>
      ) : null}
      {pages.isLoading ? <p aria-busy="true">Загружаем страницы…</p> : null}
      {pages.isError ? <Notice>Не удалось загрузить страницы.</Notice> : null}
      {!pages.isLoading && !pages.isError && !pages.data?.data.length ? (
        <EmptyState
          title="Статических страниц пока нет"
          description="Создайте первую страницу, например «Контакты» или «О компании»."
          action={
            canManage
              ? { label: "Новая страница", onClick: () => setCreating(true) }
              : undefined
          }
        />
      ) : null}
      {pages.data?.data.length ? (
        <div className="users-table-wrap">
          <table className="users-table site-pages-table">
            <thead>
              <tr>
                <th>Страница</th>
                <th>Путь</th>
                <th>Статус</th>
                <th>Обновлена</th>
                <th>Действия</th>
              </tr>
            </thead>
            <tbody>
              {pages.data.data.map((page) => (
                <tr key={page.id}>
                  <td data-label="Страница">
                    <strong>{page.title}</strong>
                    <small>{page.key}</small>
                  </td>
                  <td data-label="Путь">/{page.slug}</td>
                  <td data-label="Статус">
                    <span
                      className={`status-badge status-badge--${page.status === "published" ? "ready" : "pending"}`}
                    >
                      {page.status === "published"
                        ? "Опубликована"
                        : page.status === "archived"
                          ? "Архив"
                          : "Черновик"}
                    </span>
                  </td>
                  <td data-label="Обновлена">
                    {new Intl.DateTimeFormat("ru-RU", {
                      dateStyle: "medium",
                      timeStyle: "short",
                    }).format(new Date(page.updated_at))}
                  </td>
                  <td data-label="Действия" className="inline-actions">
                    {canManage ? (
                      <button
                        className="button button--secondary button--compact"
                        type="button"
                        onClick={() => {
                          setSelected(page);
                          setCreating(false);
                        }}
                      >
                        Изменить
                      </button>
                    ) : null}
                    {canManage ? (
                      <button
                        className="button button--text button--danger"
                        type="button"
                        disabled={remove.isPending}
                        onClick={() => {
                          if (
                            window.confirm(`Удалить страницу «${page.title}»?`)
                          )
                            remove.mutate(page.id);
                        }}
                      >
                        Удалить
                      </button>
                    ) : null}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : null}
    </main>
  );
}

function SettingsForm({
  initial,
  pending,
  onSubmit,
}: {
  initial: SiteContentSettings;
  pending: boolean;
  onSubmit: (input: SiteContentSettings) => void;
}) {
  const [value, setValue] = useState(initial);
  const update = <K extends keyof SiteContentSettings>(
    key: K,
    next: SiteContentSettings[K],
  ) => setValue((current) => ({ ...current, [key]: next }));
  return (
    <form
      className="site-content-form site-settings-form"
      onSubmit={(event) => {
        event.preventDefault();
        onSubmit(value);
      }}
    >
      <div className="site-content-form__grid">
        <label>
          Название сайта
          <input
            required
            maxLength={255}
            value={value.site_name}
            onChange={(event) => update("site_name", event.target.value)}
          />
        </label>
        <label>
          Юридическое название
          <input
            required
            maxLength={255}
            value={value.legal_name}
            onChange={(event) => update("legal_name", event.target.value)}
          />
        </label>
        <label>
          Телефон
          <input
            required
            maxLength={64}
            value={value.phone}
            onChange={(event) => update("phone", event.target.value)}
          />
        </label>
        <label>
          Почта
          <input
            required
            type="email"
            maxLength={255}
            value={value.email}
            onChange={(event) => update("email", event.target.value)}
          />
        </label>
        <label>
          Адрес
          <textarea
            required
            rows={2}
            maxLength={1000}
            value={value.address}
            onChange={(event) => update("address", event.target.value)}
          />
        </label>
        <label>
          Режим работы
          <textarea
            required
            rows={2}
            maxLength={500}
            value={value.working_hours}
            onChange={(event) => update("working_hours", event.target.value)}
          />
        </label>
        <label>
          Ссылка на карту
          <input
            type="url"
            value={value.map_url ?? ""}
            onChange={(event) => update("map_url", event.target.value || null)}
          />
        </label>
      </div>
      <details className="site-content-form__details" open>
        <summary>Значения SEO по умолчанию</summary>
        <div className="site-content-form__grid">
          <label>
            Заголовок
            <input
              required
              maxLength={255}
              value={value.default_seo_title}
              onChange={(event) =>
                update("default_seo_title", event.target.value)
              }
            />
          </label>
          <label>
            Описание
            <textarea
              required
              rows={3}
              maxLength={500}
              value={value.default_seo_description}
              onChange={(event) =>
                update("default_seo_description", event.target.value)
              }
            />
          </label>
          <label>
            Изображение Open Graph
            <input
              type="url"
              value={value.default_og_image_url ?? ""}
              onChange={(event) =>
                update("default_og_image_url", event.target.value || null)
              }
            />
          </label>
        </div>
      </details>
      <button
        className="button button--primary"
        type="submit"
        disabled={pending}
      >
        {pending ? "Сохраняем…" : "Сохранить настройки"}
      </button>
    </form>
  );
}

export function SiteSettingsPage({
  services,
  canManage,
}: {
  services: SiteContentServices;
  canManage: boolean;
}) {
  const client = useQueryClient();
  const settings = useQuery({
    queryKey: ["site", "settings"],
    queryFn: () => services.settings(),
    retry: false,
  });
  const save = useMutation({
    mutationFn: services.updateSettings,
    onSuccess: async () => {
      await client.invalidateQueries({ queryKey: ["site", "settings"] });
    },
  });
  return (
    <main
      className="content-page site-content-page"
      aria-labelledby="site-settings-title"
    >
      <header className="page-header">
        <div>
          <p className="eyebrow">Публичный сайт</p>
          <h1 id="site-settings-title">Настройки сайта</h1>
          <p>
            Контакты и значения SEO по умолчанию отображаются на публичном
            сайте. Изменения применяются после успешного ответа сервера.
          </p>
        </div>
      </header>
      {settings.isLoading ? <p aria-busy="true">Загружаем настройки…</p> : null}
      {settings.isError ? (
        <Notice>Не удалось загрузить настройки сайта.</Notice>
      ) : null}
      {save.isError ? (
        <Notice>
          {siteError(save.error, "Не удалось сохранить настройки.")}
        </Notice>
      ) : null}
      {settings.data?.data ? (
        canManage ? (
          <SettingsForm
            initial={settings.data.data}
            pending={save.isPending}
            onSubmit={(value) => save.mutate({ settings: value })}
          />
        ) : (
          <section className="site-content-panel">
            <p>
              У вас есть доступ к просмотру, но нет права изменять настройки
              сайта.
            </p>
          </section>
        )
      ) : null}
    </main>
  );
}

function uploadMime(file: File): string {
  return file.type || "application/octet-stream";
}

export function SiteMediaUpload({
  services,
  disabled = false,
  selectedMediaId,
  onSelected,
}: {
  services: SiteContentServices;
  disabled?: boolean;
  selectedMediaId?: string | null;
  onSelected: (media: {
    id: string;
    url: string | null;
    preview_url?: string | null;
    alt: string | null;
  }) => void;
}) {
  const input = useRef<HTMLInputElement>(null);
  const [alt, setAlt] = useState("");
  const [message, setMessage] = useState<string | null>(null);
  const upload = useMutation({
    mutationFn: (file: File) =>
      services.uploadMedia(
        {
          filename: file.name,
          mime_type: uploadMime(file),
          size: file.size,
          alt_text: alt.trim() || null,
        },
        file,
      ),
    onSuccess: (media) => {
      setMessage("Файл загружен. Изображение проверяется перед публикацией.");
      onSelected(media);
    },
    onError: (error) =>
      setMessage(siteError(error, "Не удалось загрузить медиафайл.")),
  });
  const choose = (event: ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    event.target.value = "";
    if (file) {
      setMessage(null);
      upload.mutate(file);
    }
  };
  return (
    <section className="site-media-upload" aria-label="Загрузка медиа">
      <div>
        <strong>Медиафайл</strong>
        <p>
          {selectedMediaId
            ? `Выбран файл ${selectedMediaId}`
            : "Выберите изображение или файл для публикации."}
        </p>
      </div>
      <label>
        Альтернативный текст
        <input
          value={alt}
          maxLength={500}
          disabled={disabled || upload.isPending}
          onChange={(event) => setAlt(event.target.value)}
        />
      </label>
      <button
        className="button button--secondary"
        type="button"
        disabled={disabled || upload.isPending}
        onClick={() => input.current?.click()}
      >
        {upload.isPending ? "Загружаем…" : "Загрузить и выбрать"}
      </button>
      <input
        ref={input}
        className="visually-hidden"
        type="file"
        aria-label="Выбрать медиафайл"
        onChange={choose}
      />
      {message ? (
        <Notice tone={upload.isError ? "error" : "info"}>{message}</Notice>
      ) : null}
    </section>
  );
}

export function SiteMediaPage({
  services,
  canManage,
}: {
  services: SiteContentServices;
  canManage: boolean;
}) {
  const [selected, setSelected] = useState<{
    id: string;
    url: string | null;
    preview_url?: string | null;
    alt: string | null;
  } | null>(null);
  const remove = useMutation({
    mutationFn: services.deleteMedia,
    onSuccess: () => setSelected(null),
  });

  return (
    <main
      className="content-page site-content-page"
      aria-labelledby="site-media-title"
    >
      <header className="page-header">
        <div>
          <p className="eyebrow">Публичный сайт</p>
          <h1 id="site-media-title">Медиа</h1>
          <p>
            Файл загружается напрямую в Garage по короткой signed-ссылке, затем
            подтверждается Core. Неподтверждённый файл не используется в
            публичных материалах.
          </p>
        </div>
      </header>
      {canManage ? (
        <SiteMediaUpload
          services={services}
          selectedMediaId={selected?.id}
          onSelected={setSelected}
        />
      ) : (
        <section className="site-content-panel">
          <p>У вас есть доступ к разделу, но нет права загружать медиафайлы.</p>
        </section>
      )}
      {selected ? (
        <section className="site-content-panel site-media-selection">
          <div>
            <h2>Выбранное медиа</h2>
            <p>{selected.alt || "Альтернативный текст не указан."}</p>
            <code>{selected.id}</code>
          </div>
          {selected.preview_url || selected.url ? (
            <img src={resolveApiUrl(selected.preview_url ?? selected.url!)} alt={selected.alt ?? ""} />
          ) : null}
          {canManage ? (
            <button
              className="button button--text button--danger"
              type="button"
              disabled={remove.isPending}
              onClick={() => {
                if (
                  window.confirm(
                    "Удалить выбранное медиа? Если оно используется в публикации, сервер отклонит операцию.",
                  )
                )
                  remove.mutate(selected.id);
              }}
            >
              {remove.isPending ? "Удаляем…" : "Удалить медиа"}
            </button>
          ) : null}
          {remove.isError ? (
            <Notice>
              {siteError(remove.error, "Не удалось удалить медиа.")}
            </Notice>
          ) : null}
        </section>
      ) : null}
    </main>
  );
}
